import { useState, useCallback } from 'react';
import supabase from '@/supabase';

export type ReportReason = 'harassment' | 'spam' | 'inappropriate_content' | 'scam' | 'other';

interface ReportUserParams {
  reportedUserId: string;
  reason: ReportReason;
  description?: string;
  conversationId?: string;
}

interface UseReportUserReturn {
  reportUser: (params: ReportUserParams) => Promise<{ success: boolean; error?: string }>;
  isLoading: boolean;
  error: string | null;
}

export function useReportUser(userId: string | undefined): UseReportUserReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reportUser = useCallback(async (params: ReportUserParams): Promise<{ success: boolean; error?: string }> => {
    if (!userId) {
      return { success: false, error: 'User must be logged in to report users' };
    }

    const { reportedUserId, reason, description, conversationId } = params;

    setIsLoading(true);
    setError(null);

    try {
      const { error: insertError } = await supabase
        .from('user_reports')
        .insert({
          reporter_id: userId,
          reported_user_id: reportedUserId,
          reason,
          description: description || null,
          conversation_id: conversationId || null,
        });

      if (insertError) {
        console.error('[useReportUser] Report error:', insertError);
        const errorMessage = insertError.message || 'Failed to submit report';
        setError(errorMessage);
        return { success: false, error: errorMessage };
      }

      return { success: true };
    } catch (err) {
      console.error('[useReportUser] Unexpected report error:', err);
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  return {
    reportUser,
    isLoading,
    error,
  };
}
